import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';

import Alert from '@/core/components/Alert';
import Button from '@/core/components/Button';
import FormContainer from '@/core/components/FormContainer';
import Header from '@/core/components/Header';
import HintMessage from '@/core/components/HintMessage';
import CheckboxInput from '@/core/components/inputs/CheckboxInput';
import TextAreaInput from '@/core/components/inputs/TextAreaInput';
import { ToastStatus } from '@/core/components/Toast/Toast';
import useForm from '@/core/hooks/useForm';
import Actions from '@/core/lib/new-architecture/actions';
import type { ReportUser } from '@/core/lib/new-architecture/actions/report.actions';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { ReportsReportProfileForm } from '@/core/types/profile';
import { reportsReportProfileFormSchema } from '@/core/types/profile';
import { ReportUserReasons } from '@/core/types/report';

interface ReportProfileFormProps {
  uuid: string;
  onClose: () => void;
}

const ReportProfileForm: React.FC<ReportProfileFormProps> = ({ uuid, onClose }) => {
  const toast = useToastContext();
  const [showError, setShowError] = useState<boolean>(false);

  const { t } = useTranslationContext(['profile.actions']);

  const { onReport, isLoading } = Actions.report.useReportUser({
    onSuccess: () => {
      toast?.open(t('report.toast.success', { ns: 'profile.actions' }), 5000, ToastStatus.SUCCESS);
      onClose();
    },
    onError: error => {
      if (error.json?.error.code === 'over_quota') {
        toast?.open(t('report.toast.over-quota', { ns: 'profile.actions' }), 5000, ToastStatus.DANGER);
        onClose();
      }
    },
  });

  const { control, onSubmit, errors, onCleanGlobalError, watch, setFocus, setValue } = useForm<null, ReportsReportProfileForm>({
    schema: reportsReportProfileFormSchema,
    mutationFn: data => {
      const body = {
        user: uuid,
        reasons: [],
      } as ReportUser;

      if (data.user_avatar) {
        body.reasons.push(ReportUserReasons.USER_AVATAR);
      }
      if (data.user_name) {
        body.reasons.push(ReportUserReasons.USER_NAME);
      }
      if (data.user_message) {
        body.reasons.push(ReportUserReasons.USER_MESSAGE);
      }
      if (data.user_other) {
        body.reasons.push(ReportUserReasons.USER_OTHER);
      }

      if (data.comment) {
        Object.assign(body, { comment: data.comment });
      }

      return onReport(body);
    },
  });

  const userAvatar = watch('user_avatar');
  const userName = watch('user_name');
  const userMessage = watch('user_message');
  const userOther = watch('user_other');
  const comment = watch('comment');

  useEffect(() => {
    if (errors.at_least_one_checked?.isError && !errors.global.isError) {
      setShowError(true);
    }
    if (userAvatar || userName || userMessage || (userOther && comment)) {
      setShowError(false);
    }
  }, [userAvatar, userName, userMessage, userOther, comment, errors]);

  const onTextAreaClick = () => {
    setValue('user_other', true);
  };

  return (
    // eslint-disable-next-line
    <div className="flex h-full flex-col gap-5" onClick={e => e.stopPropagation()}>
      <Header content={t('report.title', { ns: 'profile.actions' })} iconAfter="close" onAfter={onClose} />

      <div className="inline-flex h-full flex-col md:gap-9">
        {errors.global.isError && errors.global.message && (
          <Alert status="error" onClose={onCleanGlobalError}>
            {t(errors.global.message, { ns: 'common' })}
          </Alert>
        )}
        <FormContainer>
          <div className="mb-4 flex flex-col gap-2">
            <Controller
              name="user_avatar"
              control={control}
              defaultValue={undefined}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <CheckboxInput onChange={onChange} onBlur={onBlur} id="user_avatar" value={value ?? false}>
                    {t('report.reasons.profile-picture', { ns: 'profile.actions' })}
                  </CheckboxInput>
                  <hr className="text-stroke-tertiary" />
                </>
              )}
            />

            <Controller
              name="user_name"
              control={control}
              defaultValue={undefined}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <CheckboxInput onChange={onChange} onBlur={onBlur} id="user_name" value={value ?? false}>
                    {t('report.reasons.offensive-username', { ns: 'profile.actions' })}
                  </CheckboxInput>
                  <hr className="text-stroke-tertiary" />
                </>
              )}
            />

            <Controller
              name="user_message"
              control={control}
              defaultValue={undefined}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <CheckboxInput onChange={onChange} onBlur={onBlur} id="user_message" value={value ?? false}>
                    {t('report.reasons.offensive-language', { ns: 'profile.actions' })}
                  </CheckboxInput>
                  <hr className="text-stroke-tertiary" />
                </>
              )}
            />

            <Controller
              name="user_other"
              control={control}
              defaultValue={false}
              render={({ field: { onChange: onCheckboxChange, value: checkboxValue } }) => (
                <>
                  <CheckboxInput
                    id="user_other"
                    onChange={inputValue => {
                      if (inputValue === true) {
                        setFocus('comment');
                      }
                      onCheckboxChange(inputValue);
                    }}
                    value={checkboxValue ?? false}
                  >
                    <Controller
                      name="comment"
                      control={control}
                      render={({ field: { ref, onChange, value } }) => (
                        <TextAreaInput ref={ref} id="comment" value={value ?? ''} onChange={onChange} onClick={onTextAreaClick} placeholder={t('report.reasons.other', { ns: 'profile.actions' })} />
                      )}
                    />
                  </CheckboxInput>
                  <HintMessage hasCharCounter inputLength={comment?.length ?? 0} max={400} />
                  {errors.comment?.isError && !errors.at_least_one_checked.isError && <HintMessage message={t(errors.comment.message as string, { ns: 'common' })} isError />}
                </>
              )}
            />
          </div>
          {showError && <HintMessage message={t(errors.at_least_one_checked.message as string, { ns: 'common' })} isError />}
        </FormContainer>
        <div className="mt-auto flex w-full justify-end">
          <div className="w-full md:max-w-max">
            <Button type="submit" variant="primary" size="large" onClick={onSubmit} isLoading={isLoading}>
              {t('report.submit', { ns: 'profile.actions' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportProfileForm;
